/**
 * playline-persistence (Phase 5+ Tier 5E 第 10 段) Phase 3:
 * client-player から cloud-first 再開機構 API を呼び出す軽量 fetch wrapper。
 *
 * 設計 doctrine:
 * - client-player は core-domain 非依存、 type は独立 declaration
 * - sibling: scene-summary-editorial / scenario-builder / reader-report と同 pattern
 */
import { ApiError, type ProblemDetail } from "./client";

export type PlaylineStatus = "active" | "paused" | "completed" | "abandoned";

export interface PlaylineEntry {
  id: string;
  scenarioId: string;
  currentSceneId: string | null;
  status: PlaylineStatus | string;
  lastPlayedAt: string | null;
  deviceFingerprint: string | null;
  versionVector: number;
  isEnded?: boolean;
  isArchived?: boolean;
  createdAt?: string;
}

export interface ListPlaylinesResponse {
  playlines: PlaylineEntry[];
  total: number;
  page: number;
  limit: number;
}

export interface LatestPlaylineResponse {
  playline: PlaylineEntry | null;
}

function fallbackProblem(status: number, statusText: string): ProblemDetail {
  return {
    type: "about:blank",
    title: "Request failed",
    status,
    detail: statusText !== "" ? statusText : "Unknown error",
  };
}

async function parseOrThrow<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let problem: ProblemDetail;
    try {
      problem = (await response.json()) as ProblemDetail;
    } catch {
      problem = fallbackProblem(response.status, response.statusText);
    }
    throw new ApiError(response.status, problem);
  }
  return (await response.json()) as T;
}

/**
 * GET /api/v1/playlines/me/latest — reader 最新 playline 取得 (= 自動再開判定用)。
 * 401 で reader=null (= anonymous)、 不在で playline=null。
 */
export async function fetchLatestPlayline(
  baseUrl: string,
): Promise<LatestPlaylineResponse> {
  const response = await fetch(`${baseUrl}/playlines/me/latest`, {
    credentials: "include",
  });
  return parseOrThrow<LatestPlaylineResponse>(response);
}

/**
 * GET /api/v1/playlines/me — reader 別 playline 一覧。 status filter + pagination。
 */
export async function listPlaylines(
  baseUrl: string,
  args: { status?: PlaylineStatus; page?: number; limit?: number } = {},
): Promise<ListPlaylinesResponse> {
  const params = new URLSearchParams();
  if (args.status !== undefined) params.set("status", args.status);
  if (args.page !== undefined) params.set("page", String(args.page));
  if (args.limit !== undefined) params.set("limit", String(args.limit));
  const qs = params.toString();
  const response = await fetch(
    `${baseUrl}/playlines/me${qs.length > 0 ? `?${qs}` : ""}`,
    { credentials: "include" },
  );
  return parseOrThrow<ListPlaylinesResponse>(response);
}

/**
 * POST /api/v1/playlines/sync — client localStorage state を cloud に upload。
 * 409 で PlaylineVersionConflictError (= 古い client request)、 server state 優先で reconcile が必要。
 *
 * Phase 5: force=true で versionVector bypass + force overwrite 経路 (= conflict resolution UI 経由)。
 */
export async function syncPlayline(
  baseUrl: string,
  args: {
    playlineId: string;
    lastPlayedAt: string; // ISO 8601
    deviceFingerprint?: string;
    status?: PlaylineStatus;
    versionVector: number;
    force?: boolean;
  },
): Promise<{ id: string; versionVector: number; lastPlayedAt: string | null; status: string }> {
  const { force, ...body } = args;
  const url = `${baseUrl}/playlines/sync${force === true ? "?force=true" : ""}`;
  const response = await fetch(url, {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body),
  });
  return parseOrThrow(response);
}
